import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {Button, Col, Container, Row} from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from '../Elements/CountdownTimer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faLock, faCartPlus} from '@fortawesome/pro-light-svg-icons';
import './SalesCta.scss';
import logoTrees from 'static/logo/logo-trees-for-future-white.png';
import classNames from 'classnames';
import SalesTimerSection from 'components/Whole/TimerSection';
import {useUserState} from 'contexts/user';
import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';

const {SF_API_URL} = process.env;
const {SITE_ID} = process.env;

export function SalesDisclaimer({iconColor = 'green', ...props}) {
  return (
    <div className="disclaimer-wrap">
      <div className="disclaimer-cards">
        <img
          className="img-fluid img-cc"
          src="https://cdn.foodrevolution.org/global/images/payments-accepted-20240424.svg"
          width={1800}
          height={175}
        />
      </div>
      <div className="disclaimer-copy text-small">
        <p className={props.noGuaranteeText ? 'mb-0' : 'mb-2'}>
          <FontAwesomeIcon className={classNames(`offer-box-security-icon text-${iconColor} mr-2`)} icon={faLock} /> For
          your security, all orders are <br className="d-none d-sm-block d-md-none" />
          processed on a secured server.
        </p>
        {props.noGuaranteeText ? null : (
          <p className="mb-0">
            Your purchase is protected by our <br className="d-none d-sm-block d-md-none" />
            60-day money back guarantee.
          </p>
        )}
      </div>
    </div>
  );
}

export function CtaSales(props) {
  const offerBox = classNames(
    'offer-box offer-box-shadow px-3 py-4 px-sm-4 px-md-5 py-md-5 text-center',
    props.offerBoxBackground,
    props.offerBoxBorder
  );

  const date = new Date(`${props.timerDate}`).getTime();

  return (
    <div className={classNames(`cta-sales cta-sales-${props.className}`)}>
      <div className={offerBox}>
        {props.heading ? (
          <ReactMarkdown
            className="offer-box-heading"
            source={props.heading || 'Get instant access — even at 2 am – guaranteed!'}
            skipHtml={true}
          />
        ) : null}

        {props.offerPrice ? (
          <>
            {props.offerTextInline ? (
              <div className="offer-box-offer-text-inline">
                {props.offerTextInline} <del className="regular-price">${props.regularPrice}</del>{' '}
                <span className="offer-price">${props.offerPrice}</span>
              </div>
            ) : (
              <div className="offer-box-offer">
                <div className="offer-box-offer-text-regular">{props.regularOffer}</div>
                <div className="offer-box-offer-text">{props.offerText}</div>
              </div>
            )}
          </>
        ) : (
          <div className="offer-box-regular-price">
            <div className="offer-box-offer-text">{props.regularOffer}</div>
          </div>
        )}

        {props.noImage ? null : (
          <Row className="d-flex justify-content-center">
            <Col xs="10" md="8" xl="6">
              <img src={props.imgSrc} width={props.imgWidth} height={props.imgHeight} alt={props.imgAlt} />
            </Col>
          </Row>
        )}

        {props.savingsText ? (
          <>
            {props.savingsTextLineTwoOfferPrice ? (
              <>
                <ReactMarkdown className="offer-box-savings-text line-one" source={props.savingsText} />
                <div className="offer-box-savings-text line-two">
                  Or sign up for the monthly rate of just <del>{props.savingsTextLineTwoRegularPrice}</del>{' '}
                  {props.savingsTextLineTwoOfferPrice} per month
                </div>
              </>
            ) : (
              <ReactMarkdown className="offer-box-savings-text" source={props.savingsText} />
            )}
          </>
        ) : null}

        {props.timerText ? (
          <div className="offer-box-timer">
            <div className="offer-box-timer-text">{props.timerText}</div>
            <div className="countdown-dark">
              <CountdownTimer time={date} />
            </div>
          </div>
        ) : null}

        {props.offerPrice ? (
          <CheckoutButton className="offer-box-button" sku={props.sku} price={props.offerPrice} slug={props.slug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {props.btnText}
          </CheckoutButton>
        ) : (
          <CheckoutButton className="offer-box-button" sku={props.sku} price={props.regularPrice} slug={props.slug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {props.btnText}
          </CheckoutButton>
        )}

        <div className="offer-box-details">
          <div className="offer-box-cards">
            <img
              className="img-fluid img-cc"
              src="https://cdn.foodrevolution.org/global/images/payments-accepted-20240424.svg"
              width={1800}
              height={175}
            />
          </div>
          <div className="offer-box-security">
            <FontAwesomeIcon className="offer-box-security-icon mr-2" icon={faLock} />
            For your security, all orders are processed on a secured server.
          </div>
          <div className="offer-box-protected d-flex justify-content-center">
            Your purchase is protected by our 60-day money back guarantee.
          </div>
        </div>

        {props.paymentPlanText ? (
          <div className="offer-box-payment-plan">
            {props.paymentPlanText}
            <CheckoutButton
              color="link"
              className="btn-link-checkout ml-1"
              sku={props.sku}
              price={props.paymentPlanPrice}
              slug={props.slug}
              queryParams={{opt: 1}}
            >
              Join now for just ${props.paymentPlanPrice}/month!
            </CheckoutButton>
          </div>
        ) : null}
      </div>

      {props.salesTimer ? (
        <Row className="text-white text-center mt-5">
          <Col>
            <SalesTimerSection />
          </Col>
        </Row>
      ) : null}

      {props.noTrees ? null : (
        <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
          <Col xs="11" lg="8" className="mb-4 mb-lg-0">
            <h4 className="section-heading mb-3 text-center text-lg-left">
              Do you want a world where everyone <br className="d-none d-md-block d-lg-none" />
              has safe <br className="d-none d-xl-block" />
              and healthy food?
            </h4>
            <h6 className="section-only-heading text-center text-lg-left text-400">
              {props.wlcTrees ? (
                <>
                  We do, too! For <strong>every</strong> WHOLE Life Club{' '}
                  <strong>
                    membership <br className="d-none d-md-block d-lg-none" />
                    purchased,
                  </strong>{' '}
                  we’ll make a donation to Trees for the Future, enabling them to{' '}
                  <strong>plant another organic fruit or nut tree</strong>{' '}
                  <br className="d-none d-md-block d-lg-none" />
                  in a low-income community.
                </>
              ) : (
                <>
                  We do, too! For every new order, we make a donation to Trees for the Future, enabling them to plant
                  another organic fruit or nut tree in a <br className="d-none d-xl-block" />
                  low-income community.
                </>
              )}
            </h6>
          </Col>
          <Col xs="11" lg="3" className="text-center">
            <img className="logo-trees" src={logoTrees} />
          </Col>
        </Row>
      )}

      {props.brainOffer ? (
        <Row className="text-white d-flex align-items-center justify-content-center mt-5">
          <Col xs="12" md="10" lg="7" className="mb-4 mb-lg-0">
            <h5 className="section-heading mb-2">Plus, if you join now — from this page ONLY</h5>
            <h5 className="mt-0 text-400">
              You’ll get instant access to your{' '}
              <strong>
                Brain <br className="d-none d-sm-block d-xl-none" />
                Power Collection…
              </strong>
            </h5>
            <p>
              3 brain-boosting <strong>expert interviews</strong> and <strong>Action Checklists</strong> with{' '}
              <br className="d-none d-sm-block d-md-none d-xl-block" />
              simple steps you can follow to <strong>protect your brain today</strong> — and for{' '}
              <br className="d-none d-sm-block d-md-none d-xl-block" />
              the rest of your life.
            </p>
          </Col>
          <Col xs="10" md="8" lg="4" className="text-center">
            <img src="https://cdn.foodrevolution.org/wlc/brain-power-collection.png" />
          </Col>
        </Row>
      ) : null}
    </div>
  );
}

CtaSales.defaultProps = {
  offerBoxBackground: 'background-white',
  offerBoxBorder: 'border-dashed border-green border-2',
  imgSrc: 'https://cdn.foodrevolution.org/wlc/wlc-product-image.png',
  imgWidth: '1200',
  imgHeight: '778',
  imgAlt: 'WHOLE Life Club Package graphic',
  btnText: 'Buy Now'
};

export default function SalesBox({
  offerBoxBackground = 'white',
  offerBoxBorder = 'border-dashed border-color-green-100 border-2',
  btnText = 'Buy Now',
  imgSrc = 'https://cdn.foodrevolution.org/hbc/hbc-product_image.png',
  imgWidth = '1200',
  imgHeight = '500',
  imgAlt = 'Healthy Brain graphic',
  ...props
}) {
  return (
    <div className={classNames(`cta-sales cta-sales-${props.className}`)}>
      {props.title ? <h2 className="section-heading text-white text-center">{props.title}</h2> : null}
      {props.subTitle ? <p className="text-white text-center mb-4">{props.subTitle}</p> : null}

      <div
        className={classNames(
          `offer-box offer-box-shadow px-3 py-4 px-sm-4 px-md-5 py-md-5 text-center background-${offerBoxBackground} ${offerBoxBorder}`
        )}
      >
        {props.noImage ? null : (
          <Row className="d-flex justify-content-center">
            <Col xs="10" md="8" xl="6">
              <img src={imgSrc} width={imgWidth} height={imgHeight} alt={imgAlt} />
            </Col>
          </Row>
        )}

        {props.heading ? (
          <div className="offer-box-heading">
            {props.heading || (
              <>
                Get instant access — even <br className="d-none d-md-block d-lg-none" />
                at 2 am – guaranteed!
              </>
            )}
          </div>
        ) : null}

        {props.offerTextNoPrice ? (
          <div className="offer-box-offer">
            <div className="text-h3 text-red text-800 mt-4">{props.regularTextNoPrice}</div>
            <div className="offer-box-offer-text mt-3">{props.offerTextNoPrice}</div>
          </div>
        ) : (
          <>
            {props.offerPrice ? (
              <>
                {props.offerTextInline ? (
                  <div className="offer-box-offer-text-inline">
                    {props.offerTextInline} <del className="regular-price">${props.regularPrice}</del>{' '}
                    <span className="offer-price">${props.offerPrice}</span>
                  </div>
                ) : (
                  <div className="offer-box-offer">
                    <div className="text-h3 text-green text-800">
                      All For Just:{' '}
                      <span className="offer-box-offer-text-regular text-medium-gray">${props.regularPrice}</span>
                    </div>
                    <div className="offer-box-offer-text">
                      {props.offerText} ${props.offerPrice}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="offer-box-regular-price">
                <span className="text-black">Right Now:</span> ${props.regularPrice}
              </div>
            )}
          </>
        )}

        {props.timerText ? (
          <div className="offer-box-timer">
            <div className="offer-box-timer-text">{props.timerText}</div>
            <div className="countdown-dark">
              <CountdownTimer time={props.date} />
            </div>
          </div>
        ) : null}

        <CheckoutButton
          className="offer-box-button"
          sku={props.sku}
          price={props.offerPrice ? props.offerPrice : props.price}
          slug={props.slug}
        >
          <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {btnText}
        </CheckoutButton>

        <div className="offer-box-details">
          <div className="offer-box-cards">
            <img
              className="img-fluid img-cc"
              src="https://cdn.foodrevolution.org/global/images/payments-accepted-20240424.svg"
              width={1800}
              height={175}
            />
          </div>
          <div className="offer-box-security">
            <FontAwesomeIcon className="offer-box-security-icon mr-2" icon={faLock} />
            For your security, all orders are processed on a secured server.
          </div>
          <div className="offer-box-protected d-flex justify-content-center">
            Your purchase is protected by our 60-day money back guarantee.
          </div>
        </div>

        {props.paymentPlanPrice ? (
          <div className="offer-box-payment-plan mt-3">
            Prefer a 3-month payment plan? We’ve got you covered!
            <CheckoutButton
              color="link"
              className="btn-link-checkout ml-1"
              sku={props.sku}
              price={props.paymentPlanPrice}
              slug={props.slug}
              queryParams={{opt: 1}}
            >
              Join now for just ${props.paymentPlanPrice}/month!
            </CheckoutButton>
          </div>
        ) : null}
      </div>

      {props.trees ? (
        <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
          <Col xs="11" lg="8" className="mb-4 mb-lg-0">
            <h6 className="section-only-heading text-center text-lg-left text-400">
              For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
              fruit or nut tree in a low-income community.
            </h6>
          </Col>
          <Col xs="11" lg="3" className="text-center">
            <img className="logo-trees" src={logoTrees} />
          </Col>
        </Row>
      ) : null}
    </div>
  );
}

export function MasterclassCta247(props) {
  return (
    <SalesBox
      className="offer-247"
      regularPrice="497"
      offerPrice="247"
      sku="C-HBC"
      slug="hbc-masterclass"
      paymentPlanPrice="89"
      heading={
        <>
          Get instant access — even <br className="d-none d-md-block d-lg-none" />
          at 2 am – guaranteed!
        </>
      }
      offerText="Masterclass Special:"
      {...props}
    />
  );
}

export function SalesCta297(props) {
  return (
    <SalesBox
      className="offer-297"
      regularPrice="497"
      offerPrice="297"
      sku="C-HBC"
      slug="hbc-special"
      paymentPlanPrice="109"
      heading={
        <>
          Get instant access — even <br className="d-none d-sm-block d-lg-none" />
          at 2 am – guaranteed!
        </>
      }
      offerText="Right Now Only:"
      {...props}
    />
  );
}

export function SalesCta297Launch({time, ...props}) {
  return (
    <SalesBox
      className="offer-297"
      regularPrice="497"
      offerPrice="297"
      sku="C-HBC"
      slug="hbc-special"
      paymentPlanPrice="109"
      heading="Get instant access — even at 2 am – guaranteed!"
      offerText="Right Now Only:"
      date={time}
      timerText="Your discount expires in:"
      {...props}
    />
  );
}

export function SalesCta497(props) {
  return <SalesBox className="offer-497" regularPrice="497" sku="C-HBC" slug="hbc" paymentPlanPrice="179" {...props} />;
}

// Dynamic Sales Box For User Experience
export function DynamicSalesCta({context = 'webinar', ...props}) {
  const {email, registrationId} = useUserState();
  const phases = usePhaseState().phases || {};
  const schedule = phases[context] || {};
  const {phase, pos, time} = schedule;
  return phase === 'after' || (!email && !registrationId) ? (
    <SalesCta497 {...props} />
  ) : (
    <SalesCta297 date={time * 1000 + 345600000} timerText="Your discount expires in:" {...props} />
  );
}

export function SalesWlcAlumni(props) {
  return (
    <SalesBox
      className="wlc-hbc-alumni-127 "
      imgSrc="https://wlc.foodrevolution.org/assets/images/action-collection.png"
      regularPrice="247"
      offerPrice="127"
      sku="WLC"
      slug="wlc-hbc-alumni"
      regularTextNoPrice={
        <>
          Regular price: $29/month <br className="d-none d-md-block" />
          (that’s $348 annually) or $247/year
        </>
      }
      offerTextNoPrice={
        <>
          Special <em>Healthy Brain</em> Offer: <br className="d-none d-lg-block" />
          $19/month or $127/year!
        </>
      }
      {...props}
    />
  );
}

export function UpsellSales({
  offerBoxBackground = 'background-white',
  offerBoxBorder = 'border-dashed border-green border-2 offer-box-shadow',
  offerBoxPadding = 'px-3 py-4 px-sm-4 px-md-5 py-md-5',
  ...props
}) {
  const offerBox = classNames('offer-box text-center', offerBoxPadding, offerBoxBackground, offerBoxBorder);

  return (
    <div className={classNames(`cta-sales cta-sales-${props.className}`)}>
      <div className={offerBox}>
        {props.heading ? <div className="upsell-box-heading">{props.heading}</div> : null}

        <div className="upsell-box-confirm">
          <div className="mb-3">
            <CheckoutButton
              className="offer-box-button offer-box-button-upsell"
              sku={props.sku}
              price={props.offerPrice}
              slug={props.slug}
            >
              <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> Yes, Add to Order
            </CheckoutButton>
          </div>
          {props.text ? <div className="upsell-box-text">{props.text}</div> : null}
        </div>

        <div className="upsell-box-no">
          <div className="mb-3">
            <Button className="btn-upsell-gray">No, thanks.</Button>
          </div>
          <div className="upsell-box-no-text">
            Clicking the “No” button above will take you to the products you just got.
          </div>
        </div>

        <div className="offer-box-details">
          <div className="offer-box-cards">
            <img
              className="img-fluid img-cc"
              src="https://cdn.foodrevolution.org/global/images/payments-accepted-20240424.svg"
              width={1800}
              height={175}
            />
          </div>
          <div className="offer-box-security">
            <FontAwesomeIcon className="offer-box-security-icon mr-2" icon={faLock} />
            For your security, all orders are processed on a secured server.
          </div>
          <div className="offer-box-protected d-flex justify-content-center">
            Your purchase is protected by our 60-day money back guarantee.
          </div>
        </div>
      </div>

      {props.noTrees ? null : (
        <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
          <Col xs="11" lg="8" className="mb-4 mb-lg-0">
            <h6 className="section-only-heading text-center text-lg-left text-400">
              For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
              fruit or nut tree in a low-income community.
            </h6>
          </Col>
          <Col xs="11" lg="3" className="text-center">
            <img className="logo-trees" src={logoTrees} />
          </Col>
        </Row>
      )}
    </div>
  );
}

export function WlcUpsell(props) {
  return (
    <UpsellSales
      className="offer-wlc"
      heading="One-Time Offer Only!"
      text={
        <>
          Clicking the button above will sign you up for WHOLE Life Club for a free 3-month trial.{' '}
          <br className="d-none d-xl-block" />
          After 3 months, you’ll be auto-billed at the $19/month discounted rate.
        </>
      }
      sku="WLC"
      slug="frs20-wlc-us"
      {...props}
    />
  );
}
